// DataCache
// Generic client-side cache with in-flight deduplication.
// One instance per page, owned by CanvasCoordinator, disposed on Turbo navigation.
// Stores raw data — domain knowledge lives in adapters (fetch-events.js), not here.

const MAX_ENTRIES = 50

export class DataCache {
  constructor() {
    this._cache = new Map()      // key -> { data, timestamp }
    this._pending = new Map()    // key -> Promise (in-flight dedup)
    this._versions = new Map()   // key -> number (race condition guard)
    this._groups = new Map()     // group -> Set<key>
    this._debug = false
  }

  /**
   * Fetch data with caching and in-flight deduplication.
   *
   * @param {string} key - Deterministic cache key
   * @param {Function} fetchFn - () => Promise<data>
   * @param {Object} [options]
   * @param {number} [options.ttl=30000] - Freshness in ms
   * @param {string} [options.group] - Group name for batch invalidation
   * @param {boolean} [options.swr=false] - Stale-while-revalidate: return stale + background refetch
   * @returns {Promise<*>} Cached or fresh data
   */
  async fetch(key, fetchFn, options = {}) {
    const { ttl = 30000, group = null, swr = false } = options

    if (group) {
      if (!this._groups.has(group)) this._groups.set(group, new Set())
      this._groups.get(group).add(key)
    }

    const cached = this._cache.get(key)

    if (cached) {
      const isStale = (Date.now() - cached.timestamp) >= ttl
      if (!isStale) {
        this._log('HIT', key)
        return cached.data
      }
      if (swr) {
        this._log('STALE+SWR', key)
        this._revalidate(key, fetchFn)
        return cached.data
      }
    }

    // In-flight dedup
    if (this._pending.has(key)) {
      this._log('DEDUP', key)
      return this._pending.get(key)
    }

    // New fetch with race guard
    this._log('FETCH', key)
    const version = (this._versions.get(key) || 0) + 1
    this._versions.set(key, version)

    const promise = fetchFn()
      .then(data => {
        this._pending.delete(key)
        if (this._versions.get(key) !== version) {
          this._log('DISCARD', key)
          return data
        }
        this._store(key, data)
        return data
      })
      .catch(err => {
        this._pending.delete(key)
        throw err
      })

    this._pending.set(key, promise)
    return promise
  }

  /**
   * Invalidate a specific key or all entries.
   * Does NOT trigger re-fetches — callers are responsible.
   */
  invalidate(key) {
    if (key) {
      this._cache.delete(key)
      this._versions.delete(key)
    } else {
      this._cache.clear()
      this._versions.clear()
      this._groups.clear()
    }
  }

  /** Invalidate all entries belonging to a group. */
  invalidateGroup(group) {
    const keys = this._groups.get(group)
    if (!keys) return
    for (const key of keys) {
      this._cache.delete(key)
      this._versions.delete(key)
    }
    this._groups.delete(group)
  }

  /**
   * Full teardown — clears cache, pending, versions, and groups.
   * Called by CanvasCoordinator on turbo:before-cache.
   */
  dispose() {
    this._cache.clear()
    this._pending.clear()
    this._versions.clear()
    this._groups.clear()
  }

  /** @private */
  _store(key, data) {
    // Soft size cap — FIFO eviction if over limit
    if (this._cache.size >= MAX_ENTRIES) {
      const oldestKey = this._cache.keys().next().value
      this._cache.delete(oldestKey)
      this._removeFromGroups(oldestKey)
    }
    this._cache.set(key, { data, timestamp: Date.now() })
  }

  /** @private */
  _removeFromGroups(key) {
    for (const set of this._groups.values()) {
      set.delete(key)
    }
  }

  /** @private — background refetch, does not block caller */
  _revalidate(key, fetchFn) {
    if (this._pending.has(key)) return
    // Set pending immediately to prevent duplicate SWR triggers
    const promise = (async () => {
      const version = (this._versions.get(key) || 0) + 1
      this._versions.set(key, version)
      try {
        const data = await fetchFn()
        if (this._versions.get(key) === version) {
          this._store(key, data)
          this._log('REVALIDATED', key)
        }
      } finally {
        this._pending.delete(key)
      }
    })()
    this._pending.set(key, promise)
  }

  /** @private */
  _log(event, key) {
    if (this._debug) console.log(`[DataCache] ${event}: ${key}`)
  }
}
